import { useState } from "react";

import useDialogStore from "@store/useDialogStore";
import useSystemStore from "@store/useSystemStore";
import { APP_COLORS } from "@styles/colors";
import { cn } from "@utils/tailwind.util";
import { Checkbox } from "@ui/FormElements/Checkbox";
import { BaseDialog } from "@ui/Dialogs/BaseDialog";
import { useFeedbackOptions } from "./hooks/useFeedbackOptions";

// Mirrors the `comment` max_length on triton's ThreadContextFeedbackUpdateSerializer.
const COMMENT_MAX_LENGTH = 2000;

interface FeedbackDialogProps {
  onSubmit: (reasons: string[], comment: string) => Promise<void>;
}

export function FeedbackDialog({ onSubmit }: FeedbackDialogProps) {
  const { closeDialog } = useDialogStore();
  // Read here, not via props: openDialog stores a snapshot that won't see options load later.
  const { feedbackOptions } = useFeedbackOptions();
  const { isDarkMode } = useSystemStore();
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasOther = selectedReasons.includes("other");
  // A comment left over from a deselected "other" must not gate submission or be sent.
  const effectiveComment = hasOther ? comment : "";
  // Code points, not UTF-16 units, to match Python's len() behind the serializer's max_length.
  const commentLength = [...effectiveComment].length;
  const isCommentTooLong = commentLength > COMMENT_MAX_LENGTH;
  const errorColor = isDarkMode ? APP_COLORS.content.errorLight : APP_COLORS.content.error;

  const handleToggleReason = (code: string) => {
    setSelectedReasons((prev) =>
      prev.includes(code) ? prev.filter((r) => r !== code) : [...prev, code]
    );
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onSubmit(selectedReasons, effectiveComment);
    } finally {
      setIsSubmitting(false);
    }
    // Don't call closeDialog() here — the onSubmit callback swaps to ThankYouDialog via openDialog
  };

  return (
    <BaseDialog
      title="What was missing or incorrect?"
      titleClassName="text-base justify-center"
      showCloseButton={false}
      primaryButton={{
        label: "Submit feedback",
        callback: handleSubmit,
        disabled: selectedReasons.length === 0 || isCommentTooLong || isSubmitting,
      }}
      secondaryButton={{
        label: "Cancel",
        callback: closeDialog,
      }}
    >
      <div className="flex flex-col gap-3 px-4 pb-2">
        {feedbackOptions.map((option) => (
          <Checkbox
            key={option.code}
            id={`feedback-${option.code}`}
            label={option.label}
            isChecked={selectedReasons.includes(option.code)}
            onChange={() => handleToggleReason(option.code)}
          />
        ))}

        {hasOther && (
          <div className="flex flex-col">
            <textarea
              className={cn(
                "w-full resize-none rounded-md border bg-white dark:bg-gray-700 p-3 text-sm text-gray-700 dark:text-invertedPrimary80 placeholder:text-gray-400",
                isCommentTooLong ? "border-red-600" : "border-gray-300 dark:border-gray-600",
                "focus:border-stroke-actionPrimary dark:focus:border-stroke-actionPrimaryDark",
                "focus:outline-none focus:shadow-mainButtonFocus focus:ring-0"
              )}
              rows={3}
              placeholder="Let us know what didn't work well in this thread context."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />

            {isCommentTooLong && (
              <span
                className="mt-1.5 font-normal not-italic text-[13px]"
                style={{ color: errorColor }}
              >
                {`Maximum ${COMMENT_MAX_LENGTH} characters allowed.`}
              </span>
            )}
          </div>
        )}
      </div>
    </BaseDialog>
  );
}
